/**
 * PresencePayrollReveal — Prototype 5
 * Route: /prototypes/presence-reveal
 *
 * The only motion prototype. Presence closes the introduction, returns
 * toward its resting source, and becomes the payroll card on the COO Home.
 * The card is never a separate object arriving on top — it is what
 * Presence has settled into.
 *
 * Sequence (patient, ~2.4s total):
 *   0.0s  Presence rests at introduction position, no home visible.
 *   0.2s  Home surface begins to become visible underneath — from 0 to ~0.9
 *         opacity — but neutral, not celebratory. Presence has not moved yet.
 *   0.8s  Presence begins its migration. The path is a single continuous
 *         curve — no arc, no oscillation. The substance itself deforms as
 *         it travels: circle → soft rectangle. It is *the same body*
 *         changing shape.
 *   2.0s  Presence arrives at the card position. It settles. No bounce.
 *   2.1s  The card's textual content resolves inside the Presence
 *         footprint, softly. Not a fade-in of an overlay — a resolution
 *         of what the substance now is.
 *
 * Controls:
 *   • Play reveal — runs the sequence
 *   • Reset — returns to the still initial frame
 *
 * Laws honored:
 *   • Continuous — one element, one substance, never two forms.
 *   • Purpose — a single-direction curve, no ornament, no return path.
 *   • Equilibrium — settles, doesn't bounce.
 *   • Illumination — internal light travels with the form.
 *   • Non-competitive — no glow spike, no attention pull.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import PrototypeShell from "./PrototypeShell";
import PresenceForm from "./PresenceForm";
import { motion, useReducedMotion } from "framer-motion";

const CARD_TARGET = { top: 240, left: 120, width: 520, height: 148 };
const PRESENCE_REST_SIZE = 260;

export default function PresencePayrollReveal() {
  const reduced = useReducedMotion();
  const [phase, setPhase] = useState("rest");   // rest | revealing | settled
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const play = useCallback(() => {
    clearTimers();
    setPhase("revealing");
    // Home surface fades in, Presence begins migration ~800ms later, arrives ~2000ms
    timers.current.push(setTimeout(() => setPhase("settled"), reduced ? 400 : 2400));
  }, [reduced]);

  const reset = useCallback(() => {
    clearTimers();
    setPhase("rest");
  }, []);

  useEffect(() => () => clearTimers(), []);

  const notes = {
    communicates:
      "The COO's first look at her Home is inseparable from Presence completing the introduction. The card is not delivered to the interface — it is what Presence has become. The eye should not register a transition; it should register an arrival.",
    placeholder: [
      "Home layout underneath is a low-fidelity ghost — enough to place the card correctly. The actual COO Home is Prototype 04.",
      "The card's inner content is minimal (title, state, one line of context). This is a motion prototype, not a data prototype.",
      "Presence in this prototype is a static illuminated form re-shaped by CSS/motion. It is not the shader-driven final substance — but it obeys the same laws.",
    ],
    decisions: [
      "Total duration currently ~2.4s. Feels patient. Could be slower. Should not be faster.",
      "The Home surface becomes visible *before* Presence moves. Alternative: it becomes visible *as* Presence arrives. Currently: before, so Presence appears to descend into a place that was always there.",
      "Card content resolves after Presence settles. Alternative: content resolves during migration. Currently: after — arrival first, meaning second.",
      "No affordance for scrubbing yet. Should there be? For review, currently: replay & reset only.",
    ],
    assumptions: [
      "Reduced-motion users see a much shorter (~400ms) opacity resolution — no migration path. The card arrives directly, still not a notification, but without the migration story. Continuity of substance is preserved.",
      "The illumination on Presence does not spike or pulse during the migration. Continuous throughout.",
      "No sound in the prototype. If sound is added in final, it must obey Law #7 — never performs.",
    ],
  };

  // Motion definitions — single-direction, no oscillation
  const easing = [0.22, 0.61, 0.36, 1.0]; // slow-start, quicker middle, slow-settle
  const presenceInitial = {
    top: "50%",
    left: "50%",
    x: "-50%",
    y: "-50%",
    width: PRESENCE_REST_SIZE,
    height: PRESENCE_REST_SIZE,
    borderRadius: "50%",
  };
  const presenceSettled = {
    top: CARD_TARGET.top,
    left: CARD_TARGET.left,
    x: 0,
    y: 0,
    width: CARD_TARGET.width,
    height: CARD_TARGET.height,
    borderRadius: 22,
  };

  return (
    <PrototypeShell title="Prototype 05 · Presence → Payroll Reveal" notes={notes}>
      <section className="max-w-5xl mx-auto px-8 lg:px-12 pt-6 pb-16" data-testid="presence-reveal">
        {/* Controls */}
        <div className="flex items-center gap-3 mb-6" data-testid="reveal-controls">
          <button
            onClick={play}
            disabled={phase !== "rest"}
            data-testid="reveal-play"
            className="px-4 py-2 rounded-full bg-stone-900 text-white text-xs hover:bg-stone-800 disabled:opacity-30"
          >
            Play reveal
          </button>
          <button
            onClick={reset}
            data-testid="reveal-reset"
            className="px-4 py-2 rounded-full subtle-border bg-white text-stone-700 text-xs hover:bg-stone-50"
          >
            Reset
          </button>
          <div className="text-[11px] text-stone-500 italic ml-3">
            phase: <span className="text-stone-800" data-testid="reveal-phase">{phase}</span>{reduced && " · reduced motion"}
          </div>
        </div>

        {/* Stage — the shared canvas Presence and Home both inhabit */}
        <div className="relative h-[520px] rounded-2xl overflow-hidden bg-stone-50 subtle-border" data-testid="reveal-stage">

          {/* HOME ghost — becomes visible under Presence, does not celebrate */}
          <motion.div
            className="absolute inset-0 px-10 pt-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: phase === "rest" ? 0 : 1 }}
            transition={{ duration: reduced ? 0.2 : 0.9, ease: easing, delay: reduced ? 0 : 0.2 }}
            data-testid="reveal-home"
          >
            <div className="text-[10px] uppercase tracking-[0.25em] text-stone-400">Rainbow Interests</div>
            <h2 className="font-serif-display text-3xl text-stone-900 mt-2 leading-tight" style={{ fontWeight: 400 }}>
              Good morning, Katrina.
            </h2>
            <p className="text-xs text-stone-500 mt-2">Three things wait for you this morning. One is ready for your review.</p>
          </motion.div>

          {/* PRESENCE — one continuous body. It doesn't fade. It moves. */}
          <motion.div
            className="absolute"
            initial={presenceInitial}
            animate={phase === "settled" ? presenceSettled : presenceInitial}
            transition={
              reduced
                ? { duration: 0 }
                : { duration: 1.6, ease: easing, delay: 0.8 }
            }
            style={{ overflow: "hidden" }}
            data-testid="reveal-presence"
          >
            {/* The substance itself — same radial illumination in cube, migration, card */}
            <div
              className="w-full h-full"
              style={{
                background:
                  "radial-gradient(circle at 32% 34%, rgba(252,246,232,0.98) 0%, rgba(242,232,210,0.85) 42%, rgba(230,220,200,0.35) 82%)",
                boxShadow:
                  "inset 0 0 40px rgba(255,255,255,0.35), inset 6px 8px 22px rgba(255,255,255,0.5)",
                borderRadius: "inherit",
              }}
            />
            {/* Card content — resolves inside Presence after settling */}
            <motion.div
              className="absolute inset-0 p-5 flex flex-col justify-between"
              initial={{ opacity: 0 }}
              animate={{ opacity: phase === "settled" ? 1 : 0 }}
              transition={{ duration: 0.6, ease: easing, delay: reduced ? 0.3 : 0.15 }}
              data-testid="reveal-card-content"
            >
              <div className="flex items-baseline justify-between gap-4">
                <div className="flex items-baseline gap-2 min-w-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 shrink-0" />
                  <div className="text-[15px] text-stone-900 truncate" style={{ fontWeight: 500 }}>PLT Bi-Weekly Payroll</div>
                </div>
                <div className="text-[11px] text-emerald-700 shrink-0">Ready for your review</div>
              </div>
              <div className="text-[12px] text-stone-600 leading-relaxed">
                Damon completed preparation on Tuesday. All calculations confirmed,
                exception rows resolved. Pay period Feb 5 – Feb 18.
              </div>
            </motion.div>
          </motion.div>
        </div>

        <p className="mt-6 text-[11px] text-stone-500 italic max-w-xl">
          A paused frame at any point should read as the same substance — never
          "cube, then card." Presence is one body throughout.
        </p>
      </section>
    </PrototypeShell>
  );
}
